import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Package, FileText, BarChart3, Settings, LogOut, ChevronDown, ChevronLeft, ChevronRight, CalendarDays, PawPrint, BedDouble } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';

const navItems = [
  { path: '/panel', icon: Home, label: 'Ana Sayfa', perm: null },
  { path: '/calendar', icon: CalendarDays, label: 'Takvim', perm: 'appointments' },
  { path: '/boarding', icon: BedDouble, label: 'Konaklama', perm: 'boarding' },
  { path: '/customers', icon: Users, label: 'Müşteriler', perm: 'customers' },
  { path: '/pets', icon: PawPrint, label: 'Hastalar', perm: 'customers' },
  { path: '/stock', icon: Package, label: 'Stok', perm: 'stock' },
  { path: '/records', icon: FileText, label: 'Kayıtlar', perm: 'reports' },
  { path: '/cash', icon: BarChart3, label: 'Raporlar', perm: 'reports' },
  { path: '/settings', icon: Settings, label: 'Ayarlar', perm: 'settings' },
];

const ClinicLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, activeClinicId, switchClinic, getActiveClinicName, hasMultipleClinics, hasPermission } = useAuth();
  const [clinicOpen, setClinicOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('clinic_sidebar_collapsed');
    return saved === null ? true : saved === 'true';
  });

  const toggleCollapse = () => {
    setCollapsed(v => {
      localStorage.setItem('clinic_sidebar_collapsed', !v);
      return !v;
    });
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] lg:flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:flex-col bg-white border-r border-gray-200 h-screen sticky top-0 flex-shrink-0 transition-all ${collapsed ? 'w-16' : 'w-60'}`} data-testid="clinic-desktop-sidebar">
        {/* Clinic Header */}
        <div className="p-3 border-b border-gray-100">
          {collapsed ? (
            <img src={LOGO_URL} alt="AGROSVET" className="w-9 h-9 object-contain mx-auto" />
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                <img src={LOGO_URL} alt="AGROSVET" className="w-9 h-9 object-contain flex-shrink-0" />
                <div className="min-w-0">
                  {hasMultipleClinics() ? (
                    <button onClick={() => setClinicOpen(v => !v)}
                      className="flex items-center gap-1 text-sm font-bold text-gray-900 hover:text-[#6b6de3] transition-colors truncate"
                      data-testid="desktop-clinic-switcher">
                      <span className="truncate">{getActiveClinicName() || 'AGROSVET'}</span>
                      <ChevronDown size={14} className="text-[#8E8E93] flex-shrink-0" />
                    </button>
                  ) : (
                    <p className="text-sm font-bold text-gray-900 truncate">{getActiveClinicName() || user?.clinic_name || 'AGROSVET'}</p>
                  )}
                  <p className="text-xs text-[#8E8E93] truncate">{user?.name}</p>
                </div>
              </div>
              {clinicOpen && hasMultipleClinics() && (
                <div className="mt-2 space-y-1" data-testid="desktop-clinic-picker">
                  {(user?.clinic_names || []).map(c => (
                    <button key={c.id} onClick={() => { switchClinic(c.id); setClinicOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${activeClinicId === c.id ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700 hover:bg-gray-100'}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {navItems.filter(item => !item.perm || hasPermission(item.perm)).map(item => {
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 py-2.5 text-sm transition-all ${collapsed ? 'justify-center px-0' : 'px-4'} ${isActive ? 'bg-[#6b6de3]/10 text-[#6b6de3] font-semibold border-r-2 border-[#6b6de3]' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`desktop-nav-${item.path.replace('/', '')}`} title={item.label}>
                <item.icon size={18} />
                {!collapsed && item.label}
              </button>
            );
          })}
        </nav>

        {/* Bottom: Toggle + Logout */}
        <div className="border-t border-gray-100">
          <button onClick={toggleCollapse}
            className={`w-full flex items-center gap-2 py-2.5 text-sm text-[#8E8E93] hover:bg-gray-50 transition-all ${collapsed ? 'justify-center px-0' : 'px-4'}`}
            title={collapsed ? 'Genişlet' : 'Daralt'} data-testid="clinic-sidebar-toggle">
            {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /> Daralt</>}
          </button>
          <button onClick={logout}
            className={`w-full flex items-center gap-2 py-2.5 text-sm text-[#FF3B30] font-medium hover:bg-red-50 transition-all ${collapsed ? 'justify-center px-0' : 'px-4'}`}
            data-testid="desktop-logout-btn" title="Çıkış Yap">
            <LogOut size={16} /> {!collapsed && 'Çıkış Yap'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
};

export default ClinicLayout;
